import { DecisionAlert, DecisionSuggestion } from "../models/DecisionSupport";
import {
    PoleDashboardViewModel,
    VALUE_CODES,
    VALUE_LABELS,
    ValueCode,
    ValueScoreViewModel
} from "../models/OverviewDashboard";

export const LOW_SCORE_THRESHOLD = 5;
export const HIGH_SCORE_THRESHOLD = 7;

export const calculateDecisionSuggestion = (
    value: ValueCode,
    score?: number
): DecisionSuggestion => {
    const valueLabel = VALUE_LABELS[value];

    if (score === undefined || !Number.isFinite(score)) {
        return {
            kind: "no-data",
            value,
            title: "Donnée indisponible",
            description: `Aucune suggestion ne peut être calculée pour la valeur ${valueLabel}.`
        };
    }

    if (score < LOW_SCORE_THRESHOLD) {
        return {
            kind: "request-workshop-or-training",
            value,
            score,
            title: `Renforcer la valeur ${valueLabel}`,
            description: `Le score de ${score.toFixed(1)} / 10 suggère un besoin d'accompagnement.`,
            actionLabel: "Demander un atelier ou une formation",
            targetColumn: "needs"
        };
    }

    if (score > HIGH_SCORE_THRESHOLD) {
        return {
            kind: "propose-workshop",
            value,
            score,
            title: `Partager une bonne pratique sur ${valueLabel}`,
            description: `Le score de ${score.toFixed(1)} / 10 constitue un point fort à valoriser.`,
            actionLabel: "Proposer un atelier",
            targetColumn: "proposals"
        };
    }

    return {
        kind: "none",
        value,
        score,
        title: "Aucune suggestion particulière",
        description: `Le score de ${score.toFixed(1)} / 10 ne déclenche pas de recommandation automatique.`
    };
};

export const calculateActionableSuggestions = (
    values: Record<ValueCode, ValueScoreViewModel>
): DecisionSuggestion[] => VALUE_CODES
    .map((code) => calculateDecisionSuggestion(code, values[code]?.score))
    .filter((suggestion) =>
        suggestion.kind === "request-workshop-or-training" ||
        suggestion.kind === "propose-workshop"
    );

export const calculatePoleDecisionAlerts = (
    dashboard: PoleDashboardViewModel
): DecisionAlert[] => calculateActionableSuggestions(dashboard.poleScores)
    .map((suggestion) => ({
        id: `pole-${dashboard.pole.id}-${suggestion.value}-${suggestion.kind}`,
        scope: "pole" as const,
        organizationName: dashboard.pole.name,
        organizationReference: dashboard.pole.reference,
        suggestion
    }));

export const calculateServiceDecisionAlerts = (
    dashboard: PoleDashboardViewModel
): DecisionAlert[] => dashboard.services.flatMap((service) =>
    calculateActionableSuggestions(service.values).map((suggestion) => ({
        id: `service-${service.service.id}-${suggestion.value}-${suggestion.kind}`,
        scope: "services" as const,
        organizationName: service.service.name,
        organizationReference: service.service.reference,
        suggestion
    }))
);
